import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FlightLoungeSelection, FlightLoungesState } from "../objects/flightLounge";

const initialState: FlightLoungesState = {
    flightId: null,
    selections: [],
    terminals: {}
};

const flushRemoveState = createAction('FLUSH_REMOVE_STATE');
const smallFlush = createAction('SMALL_FLUSH');

function isSameSelection(
    selection: FlightLoungeSelection,
    segmentKey: string,
    loungeCode: string
): boolean {
    return selection.segmentKey === segmentKey && selection.loungeCode === loungeCode;
}

// Lounges picked in the "Options aéroport" step of the selected flight, kept until the flight
// is added to the cart with them.
export const flightLoungesSlice = createSlice({
    name: 'flightLounges',
    initialState,
    reducers: {
        resetFlightLounges(state, action: PayloadAction<number | null>) {
            return {
                ...initialState,
                flightId: action.payload
            };
        },
        toggleFlightLounge(state, action: PayloadAction<FlightLoungeSelection>) {
            const { segmentKey, loungeCode } = action.payload;
            if (state.selections.some((selection) => isSameSelection(selection, segmentKey, loungeCode))) {
                state.selections = state.selections.filter((selection) => {
                    return !isSameSelection(selection, segmentKey, loungeCode);
                });
            } else {
                state.selections.push(action.payload);
            }
        },
        // Unticking the last traveller removes the lounge.
        toggleFlightLoungeTraveler(
            state,
            action: PayloadAction<{ segmentKey: string, loungeCode: string, travelerId: number }>
        ) {
            const { segmentKey, loungeCode, travelerId } = action.payload;
            const selection = state.selections.find((item) => isSameSelection(item, segmentKey, loungeCode));
            if (!selection) {
                return;
            }
            if (selection.travelerIds.includes(travelerId)) {
                selection.travelerIds = selection.travelerIds.filter((id) => id !== travelerId);
            } else {
                selection.travelerIds.push(travelerId);
            }
            if (selection.travelerIds.length === 0) {
                state.selections = state.selections.filter((item) => {
                    return !isSameSelection(item, segmentKey, loungeCode);
                });
            }
        },
        clearFlightLoungeSelections(state) {
            state.selections = [];
        },
        setFlightLoungeTerminal(state, action: PayloadAction<{ segmentKey: string, terminal: string | null }>) {
            state.terminals[action.payload.segmentKey] = action.payload.terminal;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(flushRemoveState, () => initialState);
        builder.addCase(smallFlush, () => initialState);
    }
});

export const {
    resetFlightLounges,
    toggleFlightLounge,
    toggleFlightLoungeTraveler,
    clearFlightLoungeSelections,
    setFlightLoungeTerminal
} = flightLoungesSlice.actions;

export default flightLoungesSlice;
